import { useCallback, useEffect, useState } from "react";
import { PageHeader } from "../components/Layout";
import { api } from "../lib/api";
import { useRealtime } from "../lib/realtime";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Swords, Shield, Target, Skull, Bell, EyeOff, RotateCcw, Radio } from "lucide-react";
import { toast } from "sonner";

export default function StreamingMode() {
  const [status, setStatus] = useState(null);
  const [subUser, setSubUser] = useState("TestSubscriber");
  const { connected } = useRealtime(() => {});

  const refresh = useCallback(() => api.integrationsStatus().then(setStatus).catch(() => {}), []);
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [refresh]);

  const run = async (fn, label) => {
    try { await fn(); toast.success(label); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="p-5 md:p-8 max-w-5xl mx-auto">
      <PageHeader title="Streaming" subtitle="Stato delle integrazioni e strumenti di test per la live." accent="amber" />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatusCard label="OBS Overlay" state={status?.overlay} testid="connection-status-obs" note={`${status?.overlayClients ?? 0} client`} />
        <StatusCard label="Twitch" state={status?.twitch} testid="connection-status-twitch" />
        <StatusCard label="Stream Deck API" state={status?.streamdeck} testid="connection-status-streamdeck" />
        <StatusCard label="Realtime" state={connected ? "connected" : "waiting"} testid="connection-status-realtime" />
      </div>

      <h2 className="font-display text-xl font-bold uppercase tracking-wider text-amber-400 border-l-4 border-amber-500 pl-3 mb-4">Test integrazioni</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        <TestBtn icon={Swords} color="#2563eb" onClick={() => run(() => api.spin("attack"), "Ruota Attacco avviata")} testid="test-spin-attack">Test Ruota Attacco</TestBtn>
        <TestBtn icon={Shield} color="#ea580c" onClick={() => run(() => api.spin("defense"), "Ruota Difesa avviata")} testid="test-spin-defense">Test Ruota Difesa</TestBtn>
        <TestBtn icon={Target} color="#f59e0b" onClick={() => run(() => api.drawObjective("hard"), "Challenge estratta")} testid="test-objective">Test Challenge</TestBtn>
        <TestBtn icon={Skull} color="#dc2626" onClick={() => run(() => api.spin("penalty"), "Penitenza avviata")} testid="test-spin-penalty">Test Penalità</TestBtn>
        <TestBtn icon={EyeOff} color="#64748b" onClick={() => run(() => api.overlayHide(), "Overlay nascosto")} testid="test-overlay-hide">Nascondi Overlay</TestBtn>
        <TestBtn icon={RotateCcw} color="#64748b" onClick={() => run(() => api.overlayReset(), "Overlay resettato")} testid="test-overlay-reset">Reset Overlay</TestBtn>
      </div>

      <div className="bg-[#131722] border border-[#334155] rounded-xl p-5">
        <div className="flex items-center gap-2 text-amber-400 font-semibold mb-3"><Bell className="w-4 h-4" /> Simula abbonamento Twitch</div>
        <div className="flex flex-wrap gap-3">
          <Input value={subUser} onChange={(e) => setSubUser(e.target.value)} placeholder="Username simulato" className="bg-[#0b0e16] border-[#334155] text-white max-w-xs" data-testid="simulate-sub-username" />
          <Button onClick={() => run(() => api.simulateSub(subUser), `Sub simulato: ${subUser}`)} className="bg-purple-600 hover:bg-purple-500 text-white" data-testid="simulate-sub-button">
            <Radio className="w-4 h-4 mr-2" /> Simula abbonamento
          </Button>
        </div>
        <p className="text-slate-500 text-xs mt-2">Mostra "NUOVO ABBONATO" sull'overlay e gira la ruota penitenze. Gli eventi sono marcati come TEST nella cronologia.</p>
      </div>
    </div>
  );
}

function StatusCard({ label, state, testid, note }) {
  const map = {
    connected: { c: "#10b981", t: "Connesso" },
    waiting: { c: "#eab308", t: "In attesa" },
    disconnected: { c: "#ef4444", t: "Disconnesso" },
  };
  const s = map[state] || map.disconnected;
  return (
    <div className="bg-[#131722] border border-[#334155] rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-slate-300 font-semibold text-sm">{label}</span>
        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: s.c, boxShadow: `0 0 10px ${s.c}` }} />
      </div>
      <div className="font-display text-2xl font-bold uppercase mt-2" style={{ color: s.c }}>{s.t}</div>
      {note && <div className="text-xs text-slate-500 mt-1">{note}</div>}
    </div>
  );
}

function TestBtn({ icon: Icon, color, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="flex items-center gap-3 bg-[#131722] border border-[#334155] rounded-lg px-4 py-4 hover:bg-[#181d2a] hover:border-amber-500/50 transition-colors text-left">
      <div className="w-9 h-9 rounded flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}22`, border: `1px solid ${color}55` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <span className="text-white font-semibold text-sm">{children}</span>
    </button>
  );
}
